
<template>
  <NavigationBarTop />
  <div class="container">
    
    <div v-if="blog_list">
      <h2 class="page-head">Our Latest Styling Blogs</h2>
      
      <div class="search-container">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search for a blog..." 
          class="service-search-input"
        >
        <button class="search-icon-btn" @click="filterBlogs" aria-label="Search Blogs">
          <span class="search-icon">🔍</span>
        </button>
      </div>

      <div class="blog-list">
        <div class="blog-item" v-for="blog in filteredBlogs" :key="blog.id" @click="openBlog(blog)">
          
          <div class="blog-item-content">
            <h3 class="blog-title">{{ blog.title }}</h3>
            <p class="blog-content">
              {{ blog.content && blog.content.startsWith('{') ? "Click to view article contents..." : (blog.content || '').substring(0, 150) }}
            </p>
          </div>
          
          <div class="service-footer">
            <span class="blog-date">{{ formatDate(blog.date) }}</span> 
            <span class="blog-author">By {{ blog.author }}</span>
          </div>
          
        </div>
      </div>

      <p v-if="filteredBlogs.length === 0 && blogs.length != 0" class="no-results">
        No Blogs match your search query: "{{ searchQuery }}"
      </p>

      <p v-if="blogs.length == 0" class="no-results"> There are no blogs present. </p>
      
    </div>

    <ViewBlog v-if="view_blog"
        :blog-data="selectedBlog"
        @append-extra="appendExtra"
        @close-blog="closeBlog"
    />
  </div>
</template>

<script>
import NavigationBarTop from '@/components/NavigationBarTop.vue';
import ViewBlog from '@/components/ViewBlog.vue';
import axiosInstance from '@/services/AxiosClient';

export default {
    name: 'ListBlogs',
    components: {
        NavigationBarTop,
        ViewBlog
    },
    data(){
        return {
            blogs: [],
            searchQuery: "",
            selectedBlog: null,
            isLoading: true,
            error: null,
            blog_list: true,
            view_blog: false,
        }
    },
    async mounted() {
        this.fetchBlogPosts()
    },
    computed: {
        filteredBlogs() {
            const query = this.searchQuery.toLowerCase()
            if (!query) return this.blogs;
            
            return this.blogs.filter(blog => 
                (blog.title && blog.title.toLowerCase().includes(query)) ||
                (blog.author && blog.author.toLowerCase().includes(query))
            )
        },
    },
    methods: {
        filterBlogs() {},
        
        formatDate(dateString) {
            if (!dateString) return "Unknown Date";
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString; 

            return new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }).format(date);
        },

        openBlog(blogData) {
            this.selectedBlog = blogData;
            this.blog_list = false;
            this.view_blog = true;
        },
        
        closeBlog() {
            this.view_blog = false;
            this.blog_list = true;
            this.selectedBlog = null;
        },
        
        async fetchBlogPosts() {
            this.isLoading = true;
            try {
                const response = await axiosInstance.get('/blog/get-all');
                this.blogs = response.data;
            } catch(error) {
                console.error("Failed to load blog posts", error);
                this.error = "Could not load blogs at this time.";
            } finally {
                this.isLoading = false;
            }
        },
        
        async appendExtra({ blogId, type }) {
            try {
                if (type === 'views') await axiosInstance.put(`/blog/update-views/${blogId}`);
                if (type === 'likes') await axiosInstance.put(`/blog/update-likes/${blogId}`);
                
                const index = this.blogs.findIndex(b => b.id === blogId);
                if (index !== -1) {
                    if (type === 'views') this.blogs[index].views = (this.blogs[index].views || 0) + 1;
                    if (type === 'likes') this.blogs[index].likes = (this.blogs[index].likes || 0) + 1;
                }
            } catch (error) {
                console.error(`Failed to execute metric incremental updates for: ${type}`, error);
            }
        }
    }
}
</script>

<style scoped>
:root {
    --color-maroon: #5D0E1F;    
    --color-gold: #FBBF24;      
    --color-earth: #A0522D;     
    --color-dark: #1c1917;      
    --color-light: #f5f5f4;     
    --color-gray: #666;         
    --font-serif: 'Playfair Display', serif; 
    --font-sans: 'Inter', sans-serif;       
}

.container {
    max-width: 1000px; /* Tightened slightly for better readability on ultra-wides */
    margin: 0 auto;
    padding: 5rem 1.5rem; 
    font-family: var(--font-sans);
}

.page-head {
    font-family: var(--font-serif);
    text-align: center;
    font-size: 2.8rem;
    font-weight: 600;
    color: var(--color-dark);
    margin-bottom: 3rem;
    position: relative;
    padding-bottom: 0.5rem;
}

.page-head::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: var(--color-gold);
    border-radius: 2px;
}

.search-container {
    display: flex;
    justify-content: center;
    margin-bottom: 3rem;
    gap: 0.5rem;
}

.service-search-input {
    width: 100%;
    max-width: 500px;
    padding: 1rem 1.5rem;
    border: 2px solid #eaeaea;
    border-radius: 30px;
    font-size: 1rem;
    font-family: var(--font-sans);
    outline: none;
    transition: border-color 0.3s ease;
}

.service-search-input:focus { border-color: var(--color-gold); }

.search-icon-btn {
    background-color: var(--color-maroon);
    color: white;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    cursor: pointer;
    transition: background-color 0.3s ease, transform 0.3s ease;
}

.search-icon-btn:hover {
    background-color: #4a0c19;
    transform: translateY(-2px);
}

/* --- HORIZONTAL 6:1 RATIO LAYOUT --- */
.blog-list {
    display: flex;
    flex-direction: column; /* Stacks the rows */
    gap: 1rem; 
}

.blog-item {
    background-color: #fffaf0; 
    padding: 1.25rem 1.5rem; 
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04); 
    border-left: 4px solid var(--color-gold); 
    transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
    cursor: pointer;
    
    /* Forces left-to-right layout */
    display: flex;
    flex-direction: row;
    align-items: center; 
    justify-content: space-between;
    gap: 2rem;
}

.blog-item:hover {
    transform: translateX(4px); 
    background-color: #fff;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08); 
}

.blog-item-content {
    flex: 1; /* Pushes the footer to the far right */
    min-width: 0; /* Prevents flex children from overflowing */
}

.blog-title {
    font-family: var(--font-serif);
    font-size: 1.35rem; 
    font-weight: 600;
    color: var(--color-maroon);
    margin-bottom: 0.25rem; /* Tightened space to title */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis; /* Adds "..." if title is too long */
}

.blog-content {
    font-size: 0.95rem;
    color: #666;
    margin: 0;
    
    /* Strict truncation: forces the text to max 2 lines to maintain low height */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
}

.service-footer {
    display: flex;
    flex-direction: column; /* Stacks date and author vertically on the right */
    align-items: flex-end; /* Aligns them to the right edge */
    justify-content: center;
    font-size: 0.85rem;
    color: var(--color-earth);
    min-width: 120px; /* Ensures the text side doesn't crush the metadata */
    border-left: 1px solid #e2d8c3; /* Vertical separator */
    padding-left: 1.5rem;
    white-space: nowrap;
}

.blog-author {
    font-weight: 600;
    margin-top: 0.25rem;
}

.no-results {
    text-align: center;
    color: var(--color-gray);
    font-size: 1.2rem;
    margin-top: 2rem;
}

/* Mobile Responsiveness: Stack it normally on very small screens */
@media (max-width: 768px) {
    .blog-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }
    .service-footer {
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
        border-left: none;
        border-top: 1px solid #e2d8c3;
        padding-left: 0;
        padding-top: 0.75rem;
    }
    .blog-title {
        white-space: normal;
    }
}
</style>